import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../dashboard/theme";

export default function AchatFilter({ fournisseurs, onFilterChange }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Initialize with today's date
  const today = new Date();
  const [filters, setFilters] = useState({
    id_fournisseur: "",
    date_debut: today,
    date_fin: today,
    statut_paiement: "",
  });

  // Fournisseur selection dialog state
  const [openFournisseurDialog, setOpenFournisseurDialog] = useState(false);
  const [fournisseurSearchTerm, setFournisseurSearchTerm] = useState("");
  const [selectedFournisseur, setSelectedFournisseur] = useState(null);

  // Apply initial filter on component mount
  useEffect(() => {
    handleApplyFilters();
  }, []);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFilterChange(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      id_fournisseur: "",
      date_debut: today,
      date_fin: today,
      statut_paiement: "",
    };
    setFilters(clearedFilters);
    setSelectedFournisseur(null);
    onFilterChange(clearedFilters);
  };

  // Fournisseur selection handling
  const handleOpenFournisseurDialog = () => {
    setOpenFournisseurDialog(true);
  };

  const handleCloseFournisseurDialog = () => {
    setOpenFournisseurDialog(false);
    setFournisseurSearchTerm("");
  };

  const handleFournisseurSelect = (fournisseur) => {
    setSelectedFournisseur(fournisseur);
    handleFilterChange("id_fournisseur", fournisseur.id_fournisseur);
    handleCloseFournisseurDialog();
  };

  const filteredFournisseurs = fournisseurs.filter(
    (fournisseur) =>
      fournisseur.raison_sociale
        ?.toLowerCase()
        .includes(fournisseurSearchTerm.toLowerCase()) ||
      fournisseur.adresse
        ?.toLowerCase()
        .includes(fournisseurSearchTerm.toLowerCase()) ||
      fournisseur.numero_fiscal
        ?.toLowerCase()
        .includes(fournisseurSearchTerm.toLowerCase()) ||
      fournisseur.telephone?.includes(fournisseurSearchTerm) ||
      fournisseur.Code_Fournisseur?.toLowerCase().includes(
        fournisseurSearchTerm.toLowerCase()
      )
  );

  const fournisseurColumns = [
    { field: "Code_Fournisseur", headerName: "Code", width: 130 },
    { field: "raison_sociale", headerName: "Nom", width: 130 },
    { field: "adresse", headerName: "Prénom", width: 130 },
    { field: "numero_fiscal", headerName: "CIN", width: 200 },
    { field: "telephone", headerName: "Téléphone", width: 130 },
    { field: "email", headerName: "Email", width: 200 },
  ];

  const commonTextFieldStyles = {
    "& .MuiOutlinedInput-root": {
      width: "100%",
    },
    "& .MuiSelect-select": {
      width: "100%",
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 1,
          boxShadow: 1,
          mb: 2,
        }}
      >
        <Stack
          direction="row"
          spanumero_fiscalg={2}
          sx={{
            flexWrap: "wrap",
            gap: 2,
            "& > *": {
              flexGrow: 1,
              minWidth: {
                xs: "100%",
                sm: "45%",
                md: "220px",
              },
              maxWidth: {
                xs: "100%",
                sm: "45%",
                md: "220px",
              },
            },
          }}
        >
          <TextField
            label="Fournisseur"
            value={
              selectedFournisseur
                ? `${selectedFournisseur.raison_sociale} ${selectedFournisseur.numero_fiscal}`
                : ""
            }
            onClick={handleOpenFournisseurDialog}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={commonTextFieldStyles}
          />

          <DatePicker
            label="Date Début"
            value={filters.date_debut}
            onChange={(date) => handleFilterChange("date_debut", date)}
            slotProps={{
              textField: {
                fullWidth: true,
                sx: commonTextFieldStyles,
              },
            }}
          />

          <DatePicker
            label="Date Fin"
            value={filters.date_fin}
            onChange={(date) => handleFilterChange("date_fin", date)}
            slotProps={{
              textField: {
                fullWidth: true,
                sx: commonTextFieldStyles,
              },
            }}
          />

          <TextField
            select
            label="Statut Paiement"
            value={filters.statut_paiement}
            onChange={(e) =>
              handleFilterChange("statut_paiement", e.target.value)
            }
            sx={commonTextFieldStyles}
          >
            <MenuItem value="">Tous les Statuts</MenuItem>
            <MenuItem value="En attente">En attente</MenuItem>
            <MenuItem value="Payé">Payé</MenuItem>
            <MenuItem value="Partiel">Partiel</MenuItem>
          </TextField>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spanumero_fiscalg={1}
            sx={{
              minWidth: { xs: "100%", sm: "auto" },
              maxWidth: { xs: "100%", sm: "auto" },
              mt: { xs: 1, sm: 0 },
              flexGrow: 0,
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              startIcon={<FilterAltIcon />}
              onClick={handleApplyFilters}
              fullWidth={isMobile}
            >
              Appliquer
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              fullWidth={isMobile}
            >
              Réinitialiser
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Fournisseur Selection Dialog */}
      <Dialog
        open={openFournisseurDialog}
        onClose={handleCloseFournisseurDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Sélectionner un Fournisseur
          <TextField
            autoFocus
            margin="dense"
            label="Rechercher..."
            type="text"
            fullWidth
            variant="outlined"
            value={fournisseurSearchTerm}
            onChange={(e) => setFournisseurSearchTerm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, width: "100%", mt: 2 }}>
            <DataGrid
              rows={filteredFournisseurs}
              columns={fournisseurColumns}
              pageSize={5}
              getRowId={(row) => row.id_fournisseur}
              onRowClick={(params) => handleFournisseurSelect(params.row)}
              autoHeight
              sx={{
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                },
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </LocalizationProvider>
  );
}
